import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictation } from "@/components/announcement/VoiceDictation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, CheckCircle2, AlertCircle, Sparkles, Loader2, PlayCircle } from "lucide-react";

export const MAX_CHARS = 600;

function CallPreview({ message }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/announcements/preview", { message: message.trim() });
      setPreview(data.preview || "");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not build preview");
    } finally { setLoading(false); }
  };
  return (
    <div className="mt-2" data-testid="call-preview">
      <Button type="button" size="sm" variant="ghost" onClick={run} disabled={loading || message.trim().length < 5}
        className="h-7 px-2 text-xs text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" data-testid="call-preview-btn">
        {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
        Preview call message
      </Button>
      {preview !== null && (
        <div className="mt-2 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3" data-testid="call-preview-text">
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-500 mb-1">This is exactly what the call will say</div>
          <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line">{preview}</div>
        </div>
      )}
    </div>
  );
}

export function StudentPicker({
  isTeacher, presetStudents, meta, cls, setCls, sec, setSec,
  search, setSearch, filtered, picked, toggleAll, togglePick,
  message, setMessage,
}) {
  return (
    <div className="space-y-4">
      {!isTeacher && !presetStudents && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Class</label>
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="ann-class-select"><SelectValue placeholder="Pick a class"/></SelectTrigger>
              <SelectContent>
                {meta.classes.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`ann-class-opt-${c}`}>
                    {c === "LKG" || c === "UKG" || c === "Play Group" ? c : `Class ${c}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Section</label>
            <Select value={sec} onValueChange={setSec}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="ann-section-select"><SelectValue placeholder="Pick a section"/></SelectTrigger>
              <SelectContent>
                {meta.sections.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`ann-section-opt-${s}`}>Sec {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="border border-slate-200 dark:border-slate-700 rounded-xl">
        <div className="p-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search students by name or roll no…"
            className="border-0 focus-visible:ring-0 h-8 px-2"
            data-testid="ann-search"
          />
          <button
            type="button"
            onClick={toggleAll}
            className="text-xs font-semibold text-violet-600 hover:text-violet-700 px-2 py-1 rounded shrink-0"
            data-testid="ann-toggle-all"
          >
            {picked.size === filtered.length && filtered.length > 0 ? "Clear" : "Select all"}
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto p-2 space-y-1" data-testid="ann-student-list">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">
              {(!cls || !sec) && !presetStudents ? "Pick class + section to load students" : "No students match"}
            </div>
          ) : filtered.map((s) => {
            const on = picked.has(s.id);
            const validPhone = !!(s.parent_phone || "").trim();
            return (
              <label
                key={s.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${on ? "bg-violet-50 dark:bg-violet-900/30" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}
                data-testid={`ann-row-${s.id}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => togglePick(s.id)}
                  className="h-4 w-4 accent-violet-500"
                  data-testid={`ann-check-${s.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    Roll {s.roll_number} · {s.parent_name || "Parent"} · {validPhone ? s.parent_phone : <span className="text-rose-500">no phone</span>}
                  </div>
                </div>
                {!validPhone && <AlertCircle className="h-4 w-4 text-rose-500 shrink-0" title="No parent phone — will be skipped" />}
              </label>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 flex items-center justify-between">
          <span data-testid="ann-picked-count">{picked.size} selected</span>
          <span>{filtered.length} shown</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1 gap-2">
          <label className="block text-xs uppercase tracking-[0.2em] text-slate-400">Message to read aloud</label>
          <VoiceDictation maxChars={MAX_CHARS} onText={(text, max) => setMessage((m) => {
            const joined = m && m.trim() ? `${m.trim()} ${text}` : text;
            return joined.slice(0, max);
          })} />
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={MAX_CHARS}
          rows={4}
          placeholder="e.g. Dear parent, please note that the school will be closed tomorrow due to a public holiday."
          className="rounded-xl"
          data-testid="ann-message"
        />
        <div className={`text-xs mt-1 text-right ${message.length > MAX_CHARS * 0.9 ? "text-rose-500" : "text-slate-400"}`}>
          {message.length}/{MAX_CHARS}
        </div>
        <CallPreview message={message} />
      </div>
    </div>
  );
}

export function TemplatesHistoryPanel({ templates, templateId, applyTemplate, history, loadingHistory, reloadHistory }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Templates
        </div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="ann-template-list">
          {templates.length === 0 ? (
            <div className="text-xs text-slate-500">Loading templates…</div>
          ) : templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => applyTemplate(t.id)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${templateId === t.id
                ? "border-violet-400 bg-violet-50 dark:bg-violet-900/30"
                : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}
              data-testid={`ann-template-${t.id}`}
            >
              <div className="flex items-center gap-1.5">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate flex-1">{t.label || t.title || "Template"}</div>
                {t.is_system && (
                  <span className="text-[9px] font-semibold uppercase tracking-wider bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded shrink-0" title="System template">SYS</span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.template || t.message}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 flex items-center justify-between">
          <span>Recent announcements</span>
          <button type="button" onClick={reloadHistory} className="text-violet-600 hover:text-violet-700 font-semibold" data-testid="ann-reload-history">
            {loadingHistory ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
          </button>
        </div>
        <div className="text-xs text-slate-500 mb-2 flex gap-3" data-testid="ann-counters">
          <span><b className="text-slate-700 dark:text-slate-200">{history.counters?.total ?? 0}</b> total</span>
          <span className="text-emerald-600">{history.counters?.success ?? 0} sent</span>
          {(history.counters?.failed ?? 0) > 0 && <span className="text-rose-600">{history.counters.failed} failed</span>}
        </div>
        <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1" data-testid="ann-history-list">
          {history.items.length === 0 ? (
            <div className="text-xs text-slate-500">No announcement calls yet.</div>
          ) : history.items.slice(0, 12).map((c) => (
            <div key={c.id} className="text-xs border-l-2 border-violet-300 pl-2.5 py-1" data-testid={`ann-history-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">{c.student_name}</span>
                {c.status === "success"
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  : <AlertCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" title={c.error_message} />}
              </div>
              <div className="text-slate-500 line-clamp-1">{c.announcement_text}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {new Date(c.created_at).toLocaleString()} · by {c.triggered_by_name || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
